<script setup lang="ts">
import { useAppState } from '../../store/state';
import WisionLogo from '../WisionLogo.vue';
import Button from '../Button.vue';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import type { OrganizationForm } from '@nfc-api/user';
import { Vue3Lottie } from 'vue3-lottie';
import ScrollData from '../../assets/lottie/scroll-down.json';
import { event } from 'vue-gtag';
import CreateAccountData from '../../assets/lottie/create_account.json';
import ErrorData from '../../assets/lottie/error.json';

const state = useAppState();
const router = useRouter();

if (state.createdOrganization === undefined) {
  router.push({ name: 'start' });
}

const { name, address, postalNumber, location, orgNumber, ahlsellId } =
  (state?.createdOrganization as OrganizationForm) ?? {};

const scrollComplete = ref(false);
const showToolTip = ref(false);
const showAnimation = ref(false);
const creatingAccount = ref(false);
const errorCreatingAccount = ref(false);
const successCreatingAccount = ref(false);

const handeAccept = async () => {
  if (!scrollComplete.value) {
    showToolTip.value = true;
    setTimeout(() => {
      showToolTip.value = false;
    }, 1000);
    return;
  }
  creatingAccount.value = true;
  state.setTermsAcceptet(true);
  event('terms_accepted', { method: 'Google' });

  const success = await state.createAccount();
  if (!success) {
    errorCreatingAccount.value = true;
    setTimeout(() => {
      errorCreatingAccount.value = false;
      creatingAccount.value = false;
    }, 3000);
    return;
  }

  successCreatingAccount.value = true;
  setTimeout(() => {
    router.replace({ name: 'login' });
  }, 2000);
};

const onScroll = (e: any) => {
  const clientHeight = e?.target?.clientHeight;
  const scrollHeight = e?.target?.scrollHeight;
  const scrollTop = e?.target?.scrollTop;

  if (scrollTop + clientHeight + 5 >= scrollHeight) {
    scrollComplete.value = true;
  }
};

const handleOnAnimationComplete = () => (showAnimation.value = false);
const handleOnScreenClick = () => (showAnimation.value = !scrollComplete.value);
</script>

<template>
  <div
    @click="handleOnScreenClick"
    class="flex flex-col items-center w-full text-onboard-shade2 relative"
  >
    <transition
      appear
      enter-active-class="transition-opacity ease-out"
      leave-active-class="transition-opacity ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="creatingAccount"
        class="z-50 flex justify-center items-center fixed w-full h-full bg-black bg-opacity-40 backdrop-blur"
      >
        <div v-if="errorCreatingAccount" class="text-center relative w-full">
          <Vue3Lottie
            :animationData="ErrorData"
            :height="100"
            :width="100"
            :loop="true"
          />
          <span
            class="animate-pulse font-bold text-xl absolute top-[100px] left-0 w-full"
          >
            Någonting gick fel. Försök igen.
          </span>
        </div>
        <div
          v-else-if="successCreatingAccount"
          class="text-center relative w-full"
        >
          <span
            class="animate-pulse font-bold text-xl absolute top-[100px] left-0 w-full"
          >
            Kontot skapat. Skickas till inloggning.
          </span>
        </div>
        <div v-else class="text-center relative">
          <Vue3Lottie :animationData="CreateAccountData" :loop="true" />
          <span
            class="animate-pulse font-bold text-xl absolute top-[180px] left-0 w-full"
          >
            Skapar konto...
          </span>
        </div>
      </div>
    </transition>
    <div
      @scroll="onScroll"
      class="w-full overflow-auto px-10 scroll-container pb-80"
    >
      <div class="flex flex-col mt-10 items-center">
        <div v-if="showAnimation" class="absolute top-0 right-0">
          <Vue3Lottie
            :animation-data="ScrollData"
            :height="250"
            :width="30"
            :loop="false"
            :onOnComplete="() => handleOnAnimationComplete()"
          />
        </div>
        <WisionLogo :width="114" class="text-onboard-foreground" />

        <span class="text-xl mt-3 uppercase">
          {{ $t('nfc.terms.agreementTerms') }}
        </span>

        <div class="mt-2 leading-none text-sm">
          <h2 class="text-lg">
            {{ $t('nfc.terms.generalTermsTitle') }}
          </h2>
          <p>
            {{ $t('nfc.terms.version') }}
          </p>
          <p class="font-bold mt-2">
            {{ $t('nfc.terms.widecoIotPlatform') }}
          </p>
          <ul class="list-disc">
            <li>
              {{ $t('nfc.terms.accountOperation') }}
            </li>
            <li>
              {{ $t('nfc.terms.wisionVisualizationPlatform') }}
            </li>
            <li>
              {{ $t('nfc.terms.wisionDemoPlatform') }}
            </li>
            <li>
              {{ $t('nfc.terms.emailSmsAlerts') }}
            </li>
            <li>
              {{ $t('nfc.terms.serverOperationStorage') }}
            </li>
          </ul>
          <p class="my-2">
            {{ $t('nfc.terms.termsConditionsIntro') }}
          </p>
          <p class="my-2">
            {{ $t('nfc.terms.widecoSwedenIntro') }}
          </p>
          <h3 class="font-bold">
            {{ $t('nfc.terms.scopeOfAgreement') }}
          </h3>
          <p class="mb-4">
            {{ $t('nfc.terms.agreementScope') }}
          </p>
          <h3 class="font-bold">
            {{ $t('nfc.terms.extentOfRights') }}
          </h3>
          <p class="mb-2">
            {{ $t('nfc.terms.usageRights') }}
          </p>
          <p>
            {{ $t('nfc.terms.usageConditions') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('nfc.terms.contractPeriod') }}
          </h3>
          <p>
            {{ $t('nfc.terms.serviceTerm') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('common.general') }}
          </h3>
          <p>
            {{ $t('nfc.terms.generalIntro') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('nfc.terms.intellectualPropertyRights') }}
          </h3>
          <p>
            {{ $t('nfc.terms.ipRights') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('nfc.terms.license') }}
          </h3>
          <p>
            {{ $t('nfc.terms.licenseAgreement') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('nfc.terms.dataOwnership') }}
          </h3>
          <p>
            {{ $t('nfc.terms.dataOwnershipAgreement') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('nfc.terms.usage') }}
          </h3>
          <p>
            {{ $t('nfc.terms.usageAgreement') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('nfc.terms.serverOperationStorageBackup') }}
          </h3>
          <p>
            {{ $t('nfc.terms.serverOperationStorageBackupAgreement') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('nfc.terms.supportSla') }}
          </h3>
          <p>
            {{ $t('nfc.terms.supportSlaAgreement') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('nfc.terms.gdpr') }}
          </h3>
          <p>
            {{ $t('nfc.terms.gdprAgreement') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('nfc.terms.pricesPaymentTerms') }}
          </h3>
          <p>
            {{ $t('nfc.terms.pricesPaymentTermsAgreement') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('nfc.terms.limitationOfLiability') }}
          </h3>
          <p>
            {{ $t('nfc.terms.liabilityLimitationAgreement') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('nfc.terms.forceMajeure') }}
          </h3>
          <p>
            {{ $t('nfc.terms.forceMajeureAgreement') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('common.accounts') }}
          </h3>
          <p>
            {{ $t('nfc.terms.accountsAgreement') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('nfc.terms.transfer') }}
          </h3>
          <p>
            {{ $t('nfc.terms.transferAgreement') }}
          </p>
          <h3 class="font-bold mt-4">
            {{ $t('nfc.terms.choiceOfLawDisputeResolution') }}
          </h3>
          <p>
            {{ $t('nfc.terms.lawDisputeResolutionAgreement') }}
          </p>
        </div>
      </div>
    </div>
    <div
      class="absolute h-52 bottom-12 p-5 bg-onboard-background w-full text-center"
    >
      <span class="text-xl uppercase">
        {{ $t('nfc.terms.verifyInfo') }}
      </span>
      <div class="text-center text-sm">
        <p>{{ orgNumber }}</p>
        <p>{{ name }}</p>
        <p>{{ address }} {{ location }} {{ postalNumber }}</p>
        <p v-if="ahlsellId">Ahlsell Kund ID: {{ ahlsellId }}</p>
      </div>
      <div class="relative w-full">
        <div v-if="showToolTip" class="tooltip">
          {{ $t('nfc.terms.readTerms') }}
        </div>
        <Button
          @click="handeAccept"
          :class="!scrollComplete && 'border-gray-500 text-gray-500'"
          class="mt-2 h-12 text-sm transition"
        >
          {{ $t('nfc.terms.accept') }}
        </Button>
      </div>
    </div>
    <span class="text-onboard-shade1 pt-5 pb-3 mt-auto text-center">
      WISION BUILD {{ state.build }}
    </span>
  </div>
</template>
<style lang="scss" scoped>
.tooltip {
  position: absolute;
  background-color: white;
  color: black;
  z-index: 4000;
  padding: 10px 20px;
  bottom: 50px;
  left: 50%;
  transform: translate(-50%);
  border-radius: 10px;
  width: 90%;
  max-width: 300px;
}
.scroll-container {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scroll-container::-webkit-scrollbar {
  display: none;
}

.scroll-container::after {
  content: '';
  position: absolute;
  bottom: 250px;
  width: 100%;
  height: 30px;
  left: 0;
  background: linear-gradient(rgba(255, 255, 255, 0), rgb(58, 57, 62));
}

.scroll-container::before {
  content: '';
  position: absolute;
  top: 0;
  width: 100%;
  height: 50px;
  left: 0;
  background: linear-gradient(rgb(58, 57, 62), rgba(255, 255, 255, 0.001));
}
</style>
